import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { getAssistantReport } from '../../api/assistant'
import { formatReportTimestamp, groupReports, reportTimestamp } from '../../utils/reportGroups'
import AssistantCard from './AssistantCard'

const ReportsModal = ({ isOpen, onClose, reports, onDelete, retentionDays }) => {
  const { t, i18n } = useTranslation()
  const [selected, setSelected] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState(null)

  const open = useCallback(async (id) => {
    setSelectedId(id)
    setLoading(true)
    setError('')
    try {
      setSelected(await getAssistantReport(id))
    } catch (err) {
      setSelected(null)
      setError(err.message || t('assistant.error'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (!isOpen) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      setSelected(null)
      setSelectedId(null)
      setError('')
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleDelete = async (report) => {
    if (!window.confirm(t('assistant.reportDeleteConfirm', { title: report.title }))) return
    setDeletingId(report.id)
    try {
      await onDelete(report.id)
      if (report.id === selectedId) {
        setSelected(null)
        setSelectedId(null)
      }
    } catch (err) {
      setError(err.message || t('assistant.reportDeleteError'))
    } finally {
      setDeletingId(null)
    }
  }

  const groups = groupReports(reports)

  const modalContent = (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/70 px-4 py-10 backdrop-blur-2xl"
      role="dialog"
      aria-modal="true"
      aria-label={t('assistant.savedReports')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="relative z-10 flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-surface-1">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="font-display text-xl text-white">{t('assistant.savedReports')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl bg-white/[0.04] text-muted-300 transition hover:bg-white/[0.08] hover:text-white"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 sm:p-6 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <div className="space-y-4">
            {groups.length === 0 && (
              <p className="text-sm text-muted-400">{t('assistant.reportsEmpty')}</p>
            )}
            {groups.map((group) => (
              <div key={group.key}>
                <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
                  {t(`assistant.reportGroup.${group.key}`)}
                </p>
                <ul className="space-y-1.5">
                  {group.items.map((report) => (
                    <li key={report.id} className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => open(report.id)}
                        aria-current={report.id === selectedId}
                        className={`min-h-11 flex-1 cursor-pointer rounded-xl px-3 py-2 text-left text-sm transition ${
                          report.id === selectedId
                            ? 'bg-white/[0.1] text-white'
                            : 'bg-white/[0.04] text-muted-100 hover:bg-white/[0.08] hover:text-white'
                        }`}
                      >
                        <span className="block">{report.title}</span>
                        <span className="block text-xs text-muted-400">
                          {formatReportTimestamp(reportTimestamp(report), i18n.language)}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(report)}
                        disabled={deletingId === report.id}
                        aria-label={t('assistant.reportDelete')}
                        className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl text-muted-400 transition hover:bg-critical/10 hover:text-critical disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                      >
                        <span className="material-icons text-lg">delete</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {retentionDays > 0 && (
              <p className="text-xs text-muted-500">
                {t('assistant.reportRetentionHint', { days: retentionDays })}
              </p>
            )}
          </div>

          <div className="min-w-0">
            {loading && <p className="text-sm text-muted-400">{t('common.loading')}</p>}
            {error && !loading && <p className="text-sm text-critical" role="alert">{error}</p>}
            {!loading && !error && selected && (
              <div>
                <p className="font-display text-lg text-white">{selected.title}</p>
                <AssistantCard card={selected.payload} fallbackCapturedAt={selected.created_at} />
              </div>
            )}
            {!loading && !error && !selected && (
              <p className="text-sm text-muted-400">{t('assistant.reportPickHint')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null
}

export default ReportsModal
